import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Mail,
  Send,
  Loader2,
  Lock,
  Sparkles,
  Calendar,
  Eye,
  CheckCircle2,
  Settings
} from 'lucide-react';

const DAYS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

const EmailDigestSection = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [settings, setSettings] = useState({
    email_digest_enabled: false,
    digest_day: 'sunday'
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  // Load settings
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        // Check subscription
        const subResponse = await api.get('/subscription/status');
        setIsPro(subResponse.data.is_pro);
        
        if (subResponse.data.is_pro) {
          // Load notification settings
          const settingsResponse = await api.get('/notifications/settings');
          setSettings({
            email_digest_enabled: settingsResponse.data.email_digest_enabled || false,
            digest_day: settingsResponse.data.digest_day || 'sunday'
          });
        }
      } catch (error) {
        console.error('Failed to load email settings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [api, isAuthenticated]);

  const toggleDigest = async (enabled) => {
    setSaving(true);
    try {
      await api.put('/notifications/settings', {
        email_digest_enabled: enabled,
        digest_day: settings.digest_day
      });
      
      setSettings(prev => ({ ...prev, email_digest_enabled: enabled }));
      toast.success(enabled ? 'Email digests enabled!' : 'Email digests disabled');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDigestDay = async (day) => {
    setSaving(true);
    try {
      await api.put('/notifications/settings', {
        email_digest_enabled: settings.email_digest_enabled,
        digest_day: day
      });
      
      setSettings(prev => ({ ...prev, digest_day: day }));
      toast.success('Digest day updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestDigest = async () => {
    setSending(true);
    try {
      const response = await api.post('/notifications/send-digest');
      toast.success(response.data.message);
      setLastSent(new Date().toISOString());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSending(false);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const response = await api.get('/notifications/preview-digest');
      setPreviewHtml(response.data.html);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  // Show upgrade prompt for non-Pro users
  if (!isPro) {
    return (
      <Card className="border-dashed border-2 border-muted-foreground/20">
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Weekly Email Digest
            <Badge variant="secondary" className="ml-2">
              <Lock className="h-3 w-3 mr-1" />
              Pro
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-primary/50" />
            </div>
            <h3 className="font-semibold mb-2">Stay on Track with Weekly Digests</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              Get a personalized email every week with your meal plan, shopping list, budget status, and pantry alerts.
            </p>
            <Button onClick={() => navigate('/pricing')} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Weekly Email Digest
          </CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.email_digest_enabled}
              onCheckedChange={toggleDigest}
              disabled={saving}
              data-testid="digest-toggle"
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {settings.email_digest_enabled ? (
          <>
            {/* Settings */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Delivery Day</p>
                  <p className="text-xs text-muted-foreground">When to receive your digest</p>
                </div>
              </div>
              <Select value={settings.digest_day} onValueChange={updateDigestDay} disabled={saving}>
                <SelectTrigger className="w-32" data-testid="digest-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(day => (
                    <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Digest Contents */}
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-sm font-medium mb-2">Your digest includes:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  This week&apos;s meal plan
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  Shopping list summary
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  Budget status & insights
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  Expiring pantry alerts
                </li>
              </ul>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={loadPreview}
                disabled={loadingPreview}
                data-testid="preview-digest-btn"
              >
                {loadingPreview ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="h-4 w-4 mr-2" />
                )}
                Preview
              </Button>
              <Button 
                className="flex-1"
                onClick={sendTestDigest}
                disabled={sending}
                data-testid="send-digest-btn"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Now
              </Button>
            </div>
            
            {lastSent && (
              <p className="text-xs text-center text-muted-foreground">
                Last sent: {new Date(lastSent).toLocaleString()}
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <Mail className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-2">
              Email digests are disabled
            </p>
            <p className="text-xs text-muted-foreground">
              Toggle the switch above to start receiving weekly meal planning summaries
            </p>
          </div>
        )}
      </CardContent>
      
      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']">Email Preview</DialogTitle>
            <DialogDescription>
              This is how your weekly digest will look
            </DialogDescription>
          </DialogHeader>
          <div 
            className="border rounded-lg overflow-hidden"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button onClick={sendTestDigest} disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send This Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EmailDigestSection;
